<template>
  <div>
    <TerminalsModal
      v-if="showTerminalsModal"
      @close="showTerminalsModal = false"
    />
    <OnboardingDetails
      v-if="showOnboarding"
      @close="showOnboarding = false"
      @refetch="refetchAdyenAppState"
    />
    <BaseTask
      v-for="(data, idx) in _DATA"
      :key="data.heading"
      :heading="data.heading"
      :state="data.state"
      :text="data.text"
      :buttonIcon="data.buttonIcon"
      mt
      @click="
        () => {
          data.click();
          if ($gtm && idx === 2) {
            $gtm.trackEvent({ event: 'sp-verification-card' });
          }
        }
      "
      @buttonClick="data.buttonClick"
      v-test="`sp-onboarding-task-${idx + 1}`"
    />
    <div
      v-if="
        step === 2 && (company.country === 'nl' || company.country === 'de')
      "
      :class="$style.buttons"
    >
      <BaseButton
        color="inverted"
        href="https://demodesk.com/book/salonized-team/salonized-pay?utm_source=email&utm_medium=email&utm_campaign=salonized_pay"
        v-test="'sp-button-demodesk'"
      >
        {{ $t('onboarding.book_demo') }}
      </BaseButton>
      <BaseButton
        v-if="vimeoId"
        :href="`https://vimeo.com/${vimeoId}`"
        v-test="'sp-button-youtube'"
      >
        {{ $t('register.salonized_pay.landing.watch_video') }}
      </BaseButton>
    </div>
  </div>
</template>
<script
  lang="ts"
  setup
>
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandNamePay } from '@/helpers/brand-name';
import { useAdyenStore } from '@/stores/adyen';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import { vimeoId } from '../helpers';
import TerminalsModal from '../TerminalsModal.vue';
import OnboardingDetails from './OnboardingDetails.vue';

const emit = defineEmits(['generateLink']);
const props = defineProps<{
  disableSalonizers: boolean;
}>();

const { company } = useCompanyStore();

const showOnboarding = ref(false);
const { t } = useI18n();
const step = ref(0);
const showTerminalsModal = ref(false);
const { user, hasFeatureFlag } = useUserStore();
const canOnboard = computed(() => hasFeatureFlag('admin-salonized-pay'));

const { adyenAppState } = useAdyenStore();
const { refetchAdyenAppState } = useAdyenStore();

watchEffect(() => {
  if (adyenAppState) {
    if (adyenAppState.industryCode) {
      step.value = 1;
    }
  }
});

type TaskState =
  | 'default'
  | 'required'
  | 'attention'
  | 'completed'
  | 'pending'
  | 'blocked';

const _DATA = computed(() => {
  const steps = [
    {
      heading: t('register.salonized_pay.new_onboarding.step_1.heading'),
      text:
        step.value === 0 && !canOnboard.value
          ? t('register.salonized_pay.verification.ask_admin')
          : t(
              'register.salonized_pay.new_onboarding.step_1.text',
              brandNamePay(),
            ),
      state: (step.value === 0
        ? canOnboard.value
          ? 'required'
          : 'blocked'
        : 'completed') as TaskState,
      click: () => {
        showOnboarding.value = true;
      },
    },
    {
      heading: t('register.salonized_pay.new_onboarding.step_2.heading'),
      state: (step.value === 0 || (step.value === 1 && !canOnboard.value)
        ? 'blocked'
        : step.value === 1
          ? 'required'
          : 'completed') as TaskState,
      text:
        step.value === 1
          ? canOnboard.value
            ? t('register.salonized_pay.new_onboarding.step_2.text')
            : t('register.salonized_pay.verification.ask_admin')
          : '',
      click: () => {
        emit('generateLink');
      },
    },
    {
      heading: t('register.salonized_pay.new_onboarding.step_0.heading'),
      state: (props.disableSalonizers
        ? 'blocked'
        : adyenAppState.terminalModel
          ? 'completed'
          : step.value < 2
            ? 'blocked'
            : step.value === 2
              ? 'optional'
              : 'completed') as TaskState,
      text:
        step.value === 2
          ? t('register.salonized_pay.new_onboarding.step_0.text')
          : '',
      click: () => {
        showTerminalsModal.value = true;
      },
      buttonIcon: step.value > 2 ? ('edit' as const) : undefined,
      buttonClick: () => {
        showTerminalsModal.value = true;
      },
    },
  ];

  // If terminal model exists, move the last step to the beginning
  if (adyenAppState.terminalModel) {
    const lastStep = steps.pop();
    if (lastStep) {
      steps.unshift(lastStep);
    }
  }

  return steps;
});
</script>

<style
  lang="scss"
  module
>
.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;
  margin-top: $spacing;
}
</style>
